import React, { useEffect } from 'react';

import { createStyles, Group, px, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import ImageUploader from 'components/ImageUploader';
import InfoCard from 'components/InfoCard';
import CardInfoLine from 'components/InfoCard/CardInfoLine';
import InfoCardSection from 'components/InfoCard/InfoCardSection';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { AthleteRetrieve } from 'types';
import { formatGymDate } from 'utils/date-functions';
import { formatPhoneNumber } from 'utils/formatters';

const useStyles = createStyles(theme => ({
  generalContainer: {
    flexGrow: 0,
    flexShrink: 0,
    [theme.fn.smallerThan('lg')]: {
      flexGrow: 1,
    },
    height: 'fit-content',
  },
  labelGrid: {
    display: 'grid',
    rowGap: px(theme.spacing.xs) / 2,
    columnGap: theme.spacing.sm,
    alignItems: 'center',
    gridTemplateColumns: '100px auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
  },
  medicalNotes: {
    maxHeight: 150,
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
  },
}));

interface IGeneralContentProps {
  athlete: AthleteRetrieve;
  permission?: boolean;
}

const GeneralContent = ({ athlete }: IGeneralContentProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();
  const { session } = useAuth();
  const [medicalNotesOpened, { toggle: toggleMedicalNotes, close: closeMedicalNotes }] =
    useDisclosure(false);

  useEffect(() => {
    closeMedicalNotes();
  }, [athlete?.medicalNotes, closeMedicalNotes]);

  const athleteAddress = athlete?.address?.addressLines || '';
  const athleteCityStateZip = `${athlete?.address?.locality ? athlete.address?.locality + ',' : ''} ${
    athlete?.address?.region || ''
  } ${athlete?.address?.zipCode || ''}`;

  const generalContentFirstRow = (
    <div className={classes.labelGrid}>
      <CardInfoLine label='Email Address' description={athlete?.email || ''} />
      <CardInfoLine label='Phone Number' description={formatPhoneNumber(athlete?.phone)} />
      <CardInfoLine label='Home Address' description={athleteAddress} />
      <CardInfoLine label='' description={athleteCityStateZip} />
    </div>
  );

  const generalContentSecondRow = (
    <div className={classes.labelGrid}>
      <CardInfoLine label='Gender' description={athlete?.gender || ''} />
      <CardInfoLine
        label='Date of Birth'
        description={athlete?.dob ? dayjs(athlete.dob).format(dateFormat) : ''}
      />
      <CardInfoLine
        label='Member Since'
        description={
          athlete?.memberSince ? formatGymDate(athlete.memberSince, session?.gym?.timezone, dateFormat) : ''
        }
      />
      <CardInfoLine
        label='Last Check In'
        description={athlete?.lastCheckin ? dayjs(athlete.lastCheckin).format(dateFormat) : ''}
      />
    </div>
  );

  const medicalInformationContent = (
    <>
      <Title order={4}>Medical Information</Title>
      <div className={classes.labelGrid}>
        <CardInfoLine label='Emergency Contact' description={athlete?.emergencyName || ''} />
        <CardInfoLine label='Phone Number' description={formatPhoneNumber(athlete?.emergencyPhone)} />
        {athlete?.medicalNotes && (
          <>
            <Text fs='italic' c='dimmed' fz='xs' ta='right'>
              Medical Notes
            </Text>
            <Group spacing={4} onClick={toggleMedicalNotes} sx={{ cursor: 'pointer', justifySelf: 'end' }}>
              <Text fz='sm' fw={theme.other.fontWeights.semibold} c='kiloBlue'>
                {medicalNotesOpened ? 'Hide' : 'View'}
              </Text>
              <IconChevronDown
                size={16}
                color={theme.colors.kiloBlue[4]}
                style={{
                  transform: medicalNotesOpened ? 'rotate(180deg)' : 'unset',
                  transition: 'ease-in 100ms',
                }}
              />
            </Group>
          </>
        )}
      </div>
      {athlete?.medicalNotes && medicalNotesOpened && (
        <Text fz='sm' mx='xs' mb='xs' p='xs' className={classes.medicalNotes}>
          {athlete.medicalNotes}
        </Text>
      )}
    </>
  );
  const generalContent = (
    <>
      <ImageUploader athleteProp={athlete} />
      <InfoCardSection content={generalContentFirstRow} />
      <InfoCardSection content={generalContentSecondRow} />
      <InfoCardSection content={medicalInformationContent} />
    </>
  );
  return (
    <InfoCard
      header={<Title order={4}>General</Title>}
      actionButton={<AthleteStatusBadge status={athlete.athleteStatus} />}
      content={generalContent}
      className={classes.generalContainer}
    />
  );
};

export default GeneralContent;
